import {
  TaskDueFilter,
  TaskPriority,
  TaskStatusFilter,
  TaskType,
} from "@bezbolisno/contracts/tasks";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { api, expectOk } from "../../lib/api";
import { keys } from "../../lib/keys";

/** The board's URL vocabulary — same keys and values as the Next page (`?status=…&page=…`). */
export const TaskSearchSchema = z.object({
  status: TaskStatusFilter.optional(),
  priority: z.union([TaskPriority, z.literal("all")]).optional(),
  type: z.union([TaskType, z.literal("all")]).optional(),
  due: TaskDueFilter.optional(),
  assignee: z.string().max(64).optional(),
  // Numbers, not strings: the router JSON-encodes a string that would parse as a number
  // ("5" → %225%22), which would make every pager link ugly and break `?page=2` assertions.
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(100).optional(),
});
export type TaskSearch = z.infer<typeof TaskSearchSchema>;

export const DEFAULT_FILTERS = {
  status: "open",
  priority: "all",
  type: "all",
  due: "all",
  assignee: "all",
} as const;

/** URL → contract query (the API's enums do not take "all" for priority/type). */
export function toQuery(search: TaskSearch) {
  return {
    status: search.status,
    priority: search.priority === "all" ? undefined : search.priority,
    type: search.type === "all" ? undefined : search.type,
    due: search.due,
    assigneeId: search.assignee,
    page: search.page,
    pageSize: search.pageSize,
  };
}

export const tasksQuery = (search: TaskSearch) =>
  queryOptions({
    queryKey: keys.tasks(search),
    queryFn: async () => expectOk(await api.tasks.list({ query: toQuery(search) }), 200),
  });
